import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2, Hammer, TrendingUp, Target, AlertTriangle, Zap } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts';
import { ShieldMeter, StageBar } from '../components/FlipShield';
import { FLIP } from '../constants/testIds';
import { formatApiError } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${Math.round(n).toLocaleString()}`;
const fmtPct = (n) => `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;

const initialAnalyzer = {
  property_address: '12 Cottage Lane, Lekki Phase 1',
  location: 'Lekki, Lagos',
  country: 'Nigeria',
  acquisition_price: 250000,
  renovation_budget: 80000,
  target_arv: 450000,
  holding_period_months: 6,
  closing_costs_pct: 2.0,
  selling_costs_pct: 6.0,
  financing_rate_pct: 0,
  contingency_pct: 10,
  notes: '',
};

const initialVelocity = {
  neighborhood: 'Lekki Phase 1',
  country: 'Nigeria',
  property_class: 'Mid',
};

export default function FlipCommand() {
  const [tab, setTab] = useState('analyzer');
  const [form, setForm] = useState(initialAnalyzer);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const [velForm, setVelForm] = useState(initialVelocity);
  const [velLoading, setVelLoading] = useState(false);
  const [velResult, setVelResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVel = (k, v) => setVelForm((f) => ({ ...f, [k]: v }));

  const analyze = async (e) => {
    e?.preventDefault();
    setAnalyzing(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        acquisition_price: Number(form.acquisition_price),
        renovation_budget: Number(form.renovation_budget),
        target_arv: Number(form.target_arv),
        holding_period_months: Number(form.holding_period_months),
        closing_costs_pct: Number(form.closing_costs_pct),
        selling_costs_pct: Number(form.selling_costs_pct),
        financing_rate_pct: Number(form.financing_rate_pct),
        contingency_pct: Number(form.contingency_pct),
      };
      const { data } = await axios.post(`${API}/flip/analyze`, payload, { withCredentials: true });
      setResult(data);
      toast.success(`Shield score: ${data.shield_score} · Grade ${data.grade}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Analyzer offline. Retry shortly.');
    } finally {
      setAnalyzing(false);
    }
  };

  const runVelocity = async (e) => {
    e?.preventDefault();
    setVelLoading(true);
    setVelResult(null);
    try {
      const { data } = await axios.post(`${API}/flip/velocity`, velForm);
      setVelResult(data);
      toast.success(`Velocity Grade ${data.velocity_grade} · ${data.flip_demand_score}/100 demand`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Velocity tracker offline.');
    } finally {
      setVelLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-12">
        <div className="md:col-span-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <Hammer size={14} strokeWidth={1.5} /> Flip Command · ROI Intelligence
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            The acquisition<br />
            <span className="font-display italic text-[#D4AF37]">and exit weapon.</span>
          </h1>
        </div>
        <div className="md:col-span-4 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            Mercy's primary instrument for house-flipping geometry. Run the Refit Analyzer
            for ARV math, then cross-reference against neighborhood Velocity before deploying capital.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-t border-b border-[#D4AF37]/15 py-5 mb-12">
        <button
          data-testid={FLIP.tabAnalyzer}
          onClick={() => setTab('analyzer')}
          className={`flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase transition-colors ${
            tab === 'analyzer' ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-[#F5F5F5]'
          }`}
        >
          <Target size={14} strokeWidth={1.5} /> Refit Analyzer
        </button>
        <button
          data-testid={FLIP.tabVelocity}
          onClick={() => setTab('velocity')}
          className={`flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase transition-colors ${
            tab === 'velocity' ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-[#F5F5F5]'
          }`}
        >
          <TrendingUp size={14} strokeWidth={1.5} /> Market Velocity
        </button>
      </div>

      {tab === 'analyzer' && (
        <AnalyzerPanel
          form={form} set={set} analyze={analyze} analyzing={analyzing} result={result}
        />
      )}
      {tab === 'velocity' && (
        <VelocityPanel
          form={velForm} setForm={setVel} run={runVelocity} loading={velLoading} result={velResult}
        />
      )}
    </div>
  );
}

// ----------------- Analyzer Panel -----------------
const AnalyzerPanel = ({ form, set, analyze, analyzing, result }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
    <form onSubmit={analyze} className="lg:col-span-5 space-y-8">
      <Section title="Subject Property">
        <Field label="Address" testId={FLIP.address} value={form.property_address} onChange={(v) => set('property_address', v)} />
        <div className="grid grid-cols-2 gap-6">
          <Field label="Location" testId={FLIP.location} value={form.location} onChange={(v) => set('location', v)} />
          <Select label="Corridor" testId={FLIP.country} value={form.country} onChange={(v) => set('country', v)}
            options={['Nigeria', 'USA']} />
        </div>
      </Section>

      <Section title="Capital Stack">
        <NumField label="Acquisition Price ($)" testId={FLIP.acquisition} value={form.acquisition_price} onChange={(v) => set('acquisition_price', v)} />
        <NumField label="Renovation Budget ($)" testId={FLIP.renovation} value={form.renovation_budget} onChange={(v) => set('renovation_budget', v)} />
        <NumField label="Target ARV ($)" testId={FLIP.arv} value={form.target_arv} onChange={(v) => set('target_arv', v)} />
        <div className="grid grid-cols-2 gap-6">
          <NumField label="Hold (months)" testId={FLIP.holding} value={form.holding_period_months} onChange={(v) => set('holding_period_months', v)} min={1} max={36} />
          <NumField label="Financing Rate %" testId={FLIP.financing} value={form.financing_rate_pct} onChange={(v) => set('financing_rate_pct', v)} step={0.1} />
        </div>
      </Section>

      <Section title="Friction Costs">
        <div className="grid grid-cols-2 gap-6">
          <NumField label="Closing %" testId={FLIP.closingPct} value={form.closing_costs_pct} onChange={(v) => set('closing_costs_pct', v)} step={0.1} />
          <NumField label="Selling %" testId={FLIP.sellingPct} value={form.selling_costs_pct} onChange={(v) => set('selling_costs_pct', v)} step={0.1} />
          <NumField label="Contingency %" testId={FLIP.contingency} value={form.contingency_pct} onChange={(v) => set('contingency_pct', v)} step={0.5} />
        </div>
      </Section>

      <button type="submit" disabled={analyzing} data-testid={FLIP.analyzeBtn} className="btn-sovereign w-full">
        {analyzing ? <><Loader2 size={16} className="animate-spin" /> <span>Computing</span></> : <><span>Run Refit Analyzer</span> <ArrowUpRight size={16} strokeWidth={1.5} /></>}
      </button>
    </form>

    <div className="lg:col-span-7">
      {!result ? <AnalyzerEmpty /> : <AnalyzerResult result={result} />}
    </div>
  </div>
);

const AnalyzerEmpty = () => (
  <div className="sov-border p-10 bg-[#0A0A0A] h-full flex flex-col items-center justify-center text-center min-h-[600px]">
    <ShieldMeter score={0} grade="—" />
    <div className="mt-10 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">The Shield Awaits</div>
    <p className="text-sm text-[#A3A3A3] font-light leading-relaxed max-w-md">
      Submit the capital stack for any subject property. Mercy's analyzer will return
      a Shield Score, deal grade, and a sovereign verdict on whether to deploy.
    </p>
  </div>
);

const AnalyzerResult = ({ result }) => (
  <div data-testid={FLIP.resultBlock} className="space-y-6">
    {/* Shield + headline */}
    <div className="sov-border bg-[#0A0A0A] p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
      <div className="flex justify-center" data-testid={FLIP.shieldScore}>
        <ShieldMeter score={result.shield_score} grade={result.grade} />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-2">Annualized ROI</div>
        <div className="font-display text-5xl md:text-6xl text-[#D4AF37] mb-6">{result.annualized_roi_pct.toFixed(1)}%</div>
        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-[#D4AF37]/15">
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Projected Profit</div>
            <div className="font-display text-2xl text-[#F5F5F5]">{fmt$(result.profit)}</div>
          </div>
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Margin of Safety</div>
            <div className="font-display text-2xl text-[#F5F5F5]">{result.margin_of_safety_pct.toFixed(1)}%</div>
          </div>
        </div>
      </div>
    </div>

    {/* Verdict */}
    <div className="sov-border bg-[#0A0A0A] p-8">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4 flex items-center gap-2">
        <Zap size={12} strokeWidth={1.5} /> Verdict
      </div>
      <p className="font-display text-2xl md:text-3xl italic leading-snug text-[#F5F5F5] mb-6">"{result.verdict}"</p>
      <p className="text-sm text-[#A3A3A3] font-light leading-relaxed">{result.market_intel}</p>
    </div>

    {/* Capital Stack Breakdown */}
    <div className="sov-border bg-[#0A0A0A] p-8">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Capital Stack</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={[
            { name: 'Acquisition', value: result.acquisition_price },
            { name: 'Renovation', value: result.renovation_budget },
            { name: 'Closing', value: result.closing_costs },
            { name: 'Holding', value: result.holding_costs },
            { name: 'Financing', value: result.financing_costs },
            { name: 'Contingency', value: result.contingency_reserve },
            { name: 'Selling', value: result.selling_costs },
          ]}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <CartesianGrid stroke="#1a1a1a" strokeDasharray="2 4" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: '#A3A3A3', fontSize: 10, letterSpacing: '0.1em' }} tickLine={false} axisLine={{ stroke: '#2a2a2a' }} />
          <YAxis tick={{ fill: '#A3A3A3', fontSize: 10 }} tickLine={false} axisLine={false}
            tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
          <Tooltip
            contentStyle={{ background: '#050505', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 0, fontSize: 12 }}
            labelStyle={{ color: '#D4AF37', textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: 10 }}
            formatter={(v) => [fmt$(v), 'Amount']}
          />
          <Bar dataKey="value" fill="#D4AF37" />
        </BarChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-[#D4AF37]/15">
        <Metric label="Total Invested" value={fmt$(result.total_invested)} />
        <Metric label="Net Proceeds" value={fmt$(result.net_proceeds)} />
        <Metric label="Breakeven ARV" value={fmt$(result.breakeven_arv)} />
        <Metric label="Hold" value={`${result.holding_period_months} mo`} />
      </div>
    </div>

    {/* Renovation Stages */}
    <div className="sov-border bg-[#0A0A0A] p-8">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Renovation Cadence</div>
      <div className="space-y-5">
        {result.renovation_stages.map((s, i) => (
          <StageBar key={i} stage={s} pctSpent={0} />
        ))}
      </div>
      <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
        Total renovation envelope · {fmt$(result.renovation_budget)} · {result.renovation_stages.reduce((a, s) => a + s.duration_weeks, 0)} weeks
      </div>
    </div>

    {/* Risk + Upside */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="sov-border bg-[#0A0A0A] p-8">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-5 flex items-center gap-2">
          <AlertTriangle size={12} strokeWidth={1.5} /> Risk Flags
        </div>
        <ul className="space-y-3">
          {result.risk_flags.map((r, i) => (
            <li key={i} className="text-sm font-light text-[#F5F5F5] flex gap-3 border-b border-[#D4AF37]/10 pb-3">
              <span className="text-[#A3A3A3] flex-shrink-0">−</span> {r}
            </li>
          ))}
        </ul>
      </div>
      <div className="sov-border bg-[#0A0A0A] p-8">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5 flex items-center gap-2">
          <TrendingUp size={12} strokeWidth={1.5} /> Upside Levers
        </div>
        <ul className="space-y-3">
          {result.upside_levers.map((u, i) => (
            <li key={i} className="text-sm font-light text-[#F5F5F5] flex gap-3 border-b border-[#D4AF37]/10 pb-3">
              <span className="text-[#D4AF37] flex-shrink-0">+</span> {u}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// ----------------- Velocity Panel -----------------
const VelocityPanel = ({ form, setForm, run, loading, result }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
    <form onSubmit={run} className="lg:col-span-4 space-y-8">
      <Section title="Tracker Inputs">
        <Field label="Neighborhood" testId={FLIP.velocityNeighborhood} value={form.neighborhood} onChange={(v) => setForm('neighborhood', v)} />
        <Select label="Country" testId={FLIP.velocityCountry} value={form.country} onChange={(v) => setForm('country', v)}
          options={['Nigeria', 'USA']} />
        <Select label="Class" testId={FLIP.velocityClass} value={form.property_class} onChange={(v) => setForm('property_class', v)}
          options={['Entry', 'Mid', 'Luxury', 'Trophy']} />
      </Section>
      <button type="submit" disabled={loading} data-testid={FLIP.velocitySubmit} className="btn-sovereign w-full">
        {loading ? <><Loader2 size={16} className="animate-spin" /> <span>Probing</span></> : <><span>Track Velocity</span> <ArrowUpRight size={16} strokeWidth={1.5} /></>}
      </button>
    </form>

    <div className="lg:col-span-8">
      {!result ? (
        <div className="sov-border bg-[#0A0A0A] p-10 h-full flex flex-col items-center justify-center text-center min-h-[500px]">
          <TrendingUp size={28} className="text-[#D4AF37]" strokeWidth={1} />
          <div className="mt-6 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Tracker Idle</div>
          <p className="text-sm text-[#A3A3A3] font-light leading-relaxed max-w-md">
            Select a neighborhood and property class. The tracker returns absorption rate,
            days-on-market, year-over-year price velocity, and a flip-demand grade.
          </p>
        </div>
      ) : (
        <div data-testid={FLIP.velocityResult} className="space-y-6">
          <div className="sov-border bg-[#0A0A0A] p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-2">Flip Demand</div>
              <div className="font-display text-7xl text-[#D4AF37] leading-none">{result.flip_demand_score}</div>
              <div className="mt-3 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">Grade {result.velocity_grade}</div>
            </div>
            <div className="space-y-4">
              <Stat label="Days on Market" value={`${result.avg_days_on_market}d`} />
              <Stat label="Months of Supply" value={`${result.months_of_supply}`} />
              <Stat label="YoY Price" value={fmtPct(result.yoy_price_change_pct)} />
              <Stat label="Recommended Hold" value={`${result.recommended_holding_period_weeks}w`} />
            </div>
          </div>

          <div className="sov-border bg-[#0A0A0A] p-8">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Field Intel</div>
            <p className="text-base text-[#F5F5F5] font-light leading-relaxed">{result.summary}</p>
          </div>

          <div className="sov-border bg-[#0A0A0A] p-8">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">12-Month Absorption Curve</div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={result.monthly_absorption}>
                <CartesianGrid stroke="#1a1a1a" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#A3A3A3', fontSize: 10, letterSpacing: '0.1em' }} tickLine={false} axisLine={{ stroke: '#2a2a2a' }} />
                <YAxis tick={{ fill: '#A3A3A3', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#050505', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 0, fontSize: 12 }}
                  labelStyle={{ color: '#D4AF37', textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: 10 }}
                  formatter={(v) => [`${v}%`, 'Absorption']}
                />
                <Line type="monotone" dataKey="absorption_pct" stroke="#D4AF37" strokeWidth={1.5} dot={{ fill: '#D4AF37', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  </div>
);

// ---------- Small primitives ----------
const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5 border-b border-[#D4AF37]/15 pb-3">{title}</div>
    <div className="space-y-6">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">{label}</label>
    <input data-testid={testId} className="input-sovereign" value={value} onChange={(e) => onChange(e.target.value)} required />
  </div>
);

const NumField = ({ label, value, onChange, testId, step = 1, min, max }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">{label}</label>
    <input data-testid={testId} type="number" step={step} min={min} max={max} className="input-sovereign"
      value={value} onChange={(e) => onChange(e.target.value)} required />
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">{label}</label>
    <select data-testid={testId} className="input-sovereign cursor-pointer" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const Metric = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">{label}</div>
    <div className="font-display text-xl text-[#F5F5F5]">{value}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-[#D4AF37]/15 pb-3">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{label}</span>
    <span className="font-display text-xl text-[#F5F5F5]">{value}</span>
  </div>
);
